import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  LayoutDashboard, 
  FileCheck, 
  Wallet, 
  Receipt, 
  Calculator,
  Users,
  Key,
  Download,
  LogOut,
  Menu,
  X,
  Coffee,
  ChevronRight,
  ChevronLeft,
  PanelLeftClose,
  PanelLeft,
  Search,
  Link as LinkIcon,
  Settings,
  Lock
} from 'lucide-react';
import { cn } from '@/lib/utils';

const navItems = [
  { path: '/', icon: FileCheck, label: 'Shifts to Reconcile', roles: ['admin', 'user'] },
  { path: '/ledger', icon: Wallet, label: 'Cash Ledger', roles: ['admin', 'user'] },
  { path: '/expenses', icon: Receipt, label: 'Cash Movement', roles: ['admin', 'user'] },
  { path: '/cash-count', icon: Calculator, label: 'Cash Count', roles: ['admin', 'user'] },
  { path: '/export', icon: Download, label: 'Export Data', roles: ['admin', 'user'] },
  { path: '/shift-search', icon: Search, label: 'Search Missing Shifts', roles: ['admin', 'user'] },
  { divider: true },
  { path: '/periods', icon: Lock, label: 'Recon Periods', roles: ['admin'] },
  { path: '/users', icon: Users, label: 'User Management', roles: ['admin'] },
  { path: '/api-keys', icon: Key, label: 'API Keys', roles: ['admin'] },
  { path: '/api-users', icon: LinkIcon, label: 'API Users', roles: ['admin'] },
  { path: '/accounting-settings', icon: Settings, label: 'Accounting', roles: ['admin'] },
];

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', collapsed.toString());
  }, [collapsed]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const filteredNavItems = navItems.filter(item => {
    if (item.divider) return isAdmin;
    return item.roles.includes(user?.role || 'user');
  });

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-[#FBFBF9]">
        {/* Mobile header */}
        <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#1A1A1A] border-b border-[#333] z-40 flex items-center px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-toggle"
            className="text-white hover:bg-[#333]"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2 ml-3">
            <img src="/brothers-logo.png" alt="Brothers Coffee" className="h-8 w-8 rounded" />
            <span className="font-semibold text-white">Shift Recon</span>
          </div>
        </header>

        {/* Sidebar overlay (mobile) */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={cn(
          "fixed top-0 left-0 h-full bg-[#1A1A1A] border-r border-[#333] z-50 transform transition-all duration-300 ease-in-out",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0 w-64" : "-translate-x-full",
          "lg:w-64",
          collapsed && "lg:w-[68px]"
        )}>
          {/* Header */}
          <div className={cn(
            "flex items-center h-16 px-4 border-b border-[#333]",
            collapsed ? "lg:justify-center lg:px-2" : "gap-3"
          )}>
            <img src="/brothers-logo.png" alt="Brothers Coffee" className="w-10 h-10 rounded flex-shrink-0" />
            <div className={cn(
              "transition-opacity duration-200",
              collapsed ? "lg:hidden lg:opacity-0" : "opacity-100"
            )}>
              <h1 className="font-semibold text-white">Shift Recon</h1>
              <p className="text-xs text-gray-400">Brothers Coffee</p>
            </div>
          </div>

          {/* Collapse toggle button (desktop only) */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleCollapse}
            className={cn(
              "hidden lg:flex absolute -right-3 top-20 h-6 w-6 rounded-full border border-[#333] bg-[#1A1A1A] shadow-sm hover:bg-[#333] text-white z-10",
              "transition-transform duration-300"
            )}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? (
              <ChevronRight className="h-3 w-3" />
            ) : (
              <ChevronLeft className="h-3 w-3" />
            )}
          </Button>

          <ScrollArea className="h-[calc(100vh-8rem)]">
            <nav className={cn("p-3 space-y-1", collapsed && "lg:p-2")}>
              {filteredNavItems.map((item, index) => {
                if (item.divider) {
                  return <div key={`divider-${index}`} className="h-px bg-[#333] my-3" />;
                }
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                
                const linkContent = (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                      isActive 
                        ? "bg-[#D97B22] text-white" 
                        : "text-gray-300 hover:bg-[#333] hover:text-white",
                      collapsed && "lg:justify-center lg:px-2"
                    )}
                    data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className={cn(
                      "transition-opacity duration-200",
                      collapsed ? "lg:hidden lg:opacity-0 lg:w-0" : "opacity-100"
                    )}>
                      {item.label}
                    </span>
                    {isActive && !collapsed && <ChevronRight className="h-4 w-4 ml-auto hidden lg:block" />}
                  </Link>
                );

                // Show tooltip when collapsed
                if (collapsed) {
                  return (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild className="hidden lg:flex">
                        {linkContent}
                      </TooltipTrigger>
                      <TooltipContent side="right" className="hidden lg:block">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return linkContent;
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[#333] bg-[#1A1A1A]">
            <div className={cn(
              "flex items-center gap-3 px-3 py-2",
              collapsed && "lg:justify-center lg:px-0"
            )}>
              <div className="w-8 h-8 bg-[#333] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-white">
                  {user?.username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className={cn(
                "flex-1 min-w-0 transition-opacity duration-200",
                collapsed ? "lg:hidden lg:opacity-0" : "opacity-100"
              )}>
                <p className="text-sm font-medium text-white truncate">{user?.username}</p>
                <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
              </div>
              {collapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={logout}
                      className={cn(
                        "text-gray-400 hover:text-white hover:bg-[#333]",
                        collapsed && "lg:hidden"
                      )}
                      data-testid="logout-button"
                    >
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Logout</TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="text-gray-400 hover:text-white hover:bg-[#333]"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              )}
            </div>
            {/* Collapsed state logout button */}
            {collapsed && (
              <div className="hidden lg:flex justify-center mt-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={logout}
                      className="text-gray-400 hover:text-white hover:bg-[#333]"
                      data-testid="logout-button-collapsed"
                    >
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Logout</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className={cn(
          "pt-16 lg:pt-0 min-h-screen transition-all duration-300",
          collapsed ? "lg:ml-[68px]" : "lg:ml-64"
        )}>
          {children}
        </main>
      </div>
    </TooltipProvider>
  );
};

export default Layout;
